<template>
  <div>
    <div v-if="isLoading(subjectTaxHistory)" class="text-center q-py-lg">
      <q-spinner-dots size="40px" color="primary" />
      <div class="text-body2 text-grey-6 q-mt-sm">Loading tax history data...</div>
    </div>
    <div v-else-if="isError(subjectTaxHistory)" class="text-center q-py-lg">
      <q-icon name="error_outline" size="40px" color="negative" />
      <div class="text-body2 text-negative q-mt-sm">{{ subjectTaxHistory.error.message }}</div>
      <q-btn flat color="primary" label="Retry" @click="fetchTaxHistory" class="q-mt-sm" />
    </div>
    <div v-if="isLoaded(subjectTaxHistory)" class="row q-col-gutter-lg">
      <div class="col-12 col-lg-6">
        <property-report-card
          title="Property Information"
          description="High level details about the subject property."
          :printing-mode="printingMode"
        >
          <q-markup-table flat dense bordered>
            <tbody>
              <tr>
                <td class="text-grey-7">Tax Assessor IDs</td>
                <td>
                  <q-btn clickable color="primary" dense outline size="sm" no-caps class="q-px-sm">
                    {{ subjectTaxHistory.data.tax_assessor_ids.length }} parcels found
                    <i class="material-icons hd-icon">arrow_drop_down</i>
                  </q-btn>
                  <q-menu transition-duration="100" style="max-width: 400px">
                    <q-card>
                      <q-card-section>
                        <div class="text-bold">Parcels</div>
                      </q-card-section>
                      <q-separator></q-separator>
                      <q-markup-table dense flat>
                        <tbody>
                          <tr v-for="x in subjectTaxHistory.data.tax_assessor_ids" :key="x">
                            <td>{{ x }}</td>
                          </tr>
                        </tbody>
                      </q-markup-table>
                    </q-card>
                  </q-menu>
                </td>
              </tr>
              <tr v-if="subjectTaxHistory.data.tax_assessor_history_v2.length > 0">
                <td class="text-grey-7">Building SF</td>
                <td>
                  {{
                    formatNumber(subjectTaxHistory.data.tax_assessor_history_v2[0].building_sq_ft)
                  }}
                  ft²
                </td>
              </tr>
              <tr v-if="subjectTaxHistory.data.tax_assessor_history_v2.length > 0">
                <td class="text-grey-7">Lot Size</td>
                <td>
                  {{
                    formatNumber(subjectTaxHistory.data.tax_assessor_history_v2[0].lot_size_sq_ft)
                  }}
                  ft²
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </property-report-card>
      </div>
      <div v-if="subjectTaxHistory.data.tax_assessor_owner_v2.length > 0" class="col-12 col-lg-6">
        <property-report-card
          title="Property Ownership"
          description="The latest ownership information of the subject property."
          :printing-mode="printingMode"
        >
          <q-markup-table flat dense bordered>
            <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
              <tr>
                <th class="text-left">Owner Name</th>
                <th class="text-right">Latest Deed Name</th>
                <th class="text-right">Owner Type</th>
                <th class="text-right">Entity Type</th>
                <th class="text-right">Vesting Relation</th>
                <th class="text-right">Trust Type</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(owner, index) in subjectTaxHistory.data.tax_assessor_owner_v2"
                :key="index"
              >
                <td class="text-left">{{ getOwnerDisplayName(owner) }}</td>
                <td class="text-right">{{ owner.owner_name_latest_deed }}</td>
                <td class="text-right">{{ owner.owner_type }}</td>
                <td class="text-right">
                  <div>
                    <q-badge
                      :color="owner.is_owner_company ? 'primary' : 'secondary'"
                      :label="owner.is_owner_company ? 'Company' : 'Individual'"
                    />
                  </div>
                </td>
                <td class="text-right">{{ owner.ownership_vesting_relation_code }}</td>
                <td class="text-right">{{ owner.owner_trust_type_code }}</td>
              </tr>
            </tbody>
          </q-markup-table>
        </property-report-card>
      </div>
      <div v-if="subjectTaxHistory.data.tax_assessor_history_v2.length > 0" class="col-12">
        <property-report-card
          title="Tax Assessment History"
          description="The assessed, market and tax bill amounts over time for the address of the subject property."
          :printing-mode="printingMode"
        >
          <q-separator />
          <div class="row q-col-gutter-md q-pa-md">
            <div class="col-12">
              <div class="row items-center q-mb-md">
                <q-select
                  v-model="selectedParcelView"
                  :options="parcelViewOptions"
                  transition-duration="100"
                  style="width: 280px"
                  dense
                  outlined
                  label="Parcel"
                  emit-value
                  map-options
                  @update:model-value="updateChart"
                />
              </div>
            </div>
            <div class="col-12 col-lg-6">
              <canvas ref="taxHistoryChart" height="300"></canvas>
            </div>
            <div class="col-12 col-lg-6">
              <q-markup-table flat dense bordered>
                <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
                  <tr>
                    <th class="text-left">Year</th>
                    <th class="text-right">Assessed Land</th>
                    <th class="text-right">Assessed Building</th>
                    <th class="text-right">Assessed Total</th>
                    <th class="text-right">Market Total</th>
                    <th class="text-right">Tax Bill</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(hist, index) in displayedTaxHistory" :key="index">
                    <tr :class="{ 'text-bold': index === displayedTaxHistory.length - 1 }">
                      <td class="text-left">
                        <div>
                          {{ hist.assessed_tax_year }}
                          <q-icon
                            v-if="hist.assessed_tax_year === subjectDetail?.year_built"
                            color="grey-7"
                            name="construction"
                          >
                            <q-tooltip> This is the year that the subject was built. </q-tooltip>
                          </q-icon>
                        </div>
                      </td>
                      <td class="text-right">{{ formatMoney(hist.assessed_value_land) }}</td>
                      <td class="text-right">
                        {{ formatMoney(hist.assessed_value_improvements) }}
                      </td>
                      <td class="text-right">
                        {{ formatMoney(hist.assessed_value_total) }}
                      </td>
                      <td class="text-right">
                        {{ formatMoney(hist.market_value_total) }}
                      </td>
                      <td class="text-right">
                        {{ formatMoney(hist.tax_bill_amount, 0) }}
                      </td>
                    </tr>
                    <tr
                      v-if="
                        index === displayedTaxHistory.length - 1 &&
                        subjectDetail?.year_built > hist.assessed_tax_year
                      "
                    >
                      <td class="text-left">{{ subjectDetail?.year_built }}</td>
                      <td colspan="5" class="text-center">
                        <div class="row items-center justify-center q-gutter-x-xs text-grey-7">
                          <q-icon name="construction"> </q-icon>
                          <span> Subject built this year </span>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </q-markup-table>
            </div>
          </div>
        </property-report-card>
      </div>
      <div v-if="subjectTaxHistory.data.recorder_v2_summary_v2.length > 0" class="col-12">
        <property-report-card
          title="Latest Deed"
          description="The latest deed data for the subject property."
          :printing-mode="printingMode"
        >
          <q-markup-table flat bordered dense>
            <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
              <tr>
                <th class="text-left">Deed Amount</th>
                <th class="text-left">Deed Date</th>
                <th class="text-left">Grantor</th>
                <th class="text-left">Grantee</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(record, index) in subjectTaxHistory.data.recorder_v2_summary_v2"
                :key="index"
              >
                <td class="text-left">{{ formatMoney(record.cherre_latest_deed_amount) }}</td>
                <td class="text-left">{{ formatDate(record.cherre_latest_deed_date) }}</td>
                <td class="text-left">{{ record.cherre_latest_deed_grantor }}</td>
                <td class="text-left">{{ record.cherre_latest_deed_grantee }}</td>
              </tr>
            </tbody>
          </q-markup-table>
        </property-report-card>
      </div>
      <div v-if="subjectTaxHistory.data.recorder_v2_summary_v2.length > 0" class="col-12">
        <property-report-card
          title="Latest Mortgage"
          description="The latest mortgage data for the subject property."
          :printing-mode="printingMode"
        >
          <template v-if="!hasMortgageData">
            <q-separator />
            <div class="column justify-center items-center q-py-xl text-grey-7">
              <q-icon name="account_balance" size="xl" class="q-mb-md" />
              <div class="text-h6">No mortgage data available</div>
              <div class="text-body2">There is no mortgage data on record for this property.</div>
            </div>
          </template>
          <template v-else>
            <q-markup-table flat bordered dense>
              <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
                <tr>
                  <th class="text-left">Mortgage Amount</th>
                  <th class="text-left">Mortgage Date</th>
                  <th class="text-left">Interest Rate</th>
                  <th class="text-left">Lender</th>
                  <th class="text-left">Interest Type</th>
                  <th class="text-left">Term</th>
                  <th class="text-left">Term Type</th>
                  <th class="text-left">Borrower</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(record, index) in recordsWithMortgageData" :key="index">
                  <td class="text-left">{{ formatMoney(record.cherre_latest_mortgage_amount) }}</td>
                  <td class="text-left">{{ formatDate(record.cherre_latest_mortgage_date) }}</td>
                  <td class="text-left">
                    {{
                      record.cherre_latest_mortgage_interest_rate
                        ? formatPercent(record.cherre_latest_mortgage_interest_rate / 100, 2)
                        : ''
                    }}
                  </td>
                  <td class="text-left">{{ record.cherre_latest_mortgage_lender }}</td>
                  <td class="text-left">{{ record.cherre_latest_mortgage_interest_type }}</td>
                  <td class="text-left">{{ record.cherre_latest_mortgage_term }}</td>
                  <td class="text-left">{{ record.cherre_latest_mortgage_term_type }}</td>
                  <td class="text-left">{{ record.cherre_latest_mortgage_borrower }}</td>
                </tr>
              </tbody>
            </q-markup-table>
          </template>
        </property-report-card>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Chart as ChartJS } from 'chart.js'
import { unitTypeColor, formatMoney, formatNumber, formatPercent } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import type { PropType } from 'vue'
import type { PropertyDetailsResponse, TaxAssessorOwnerV2 } from 'common'
import { isLoaded, isLoading, isError, isNotStarted } from '@/utils/loading'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { format, parseISO } from 'date-fns'

export default {
  name: 'PropertyCompareTaxHistory',
  components: { PropertyReportCard },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartInstance: null as ChartJS | null,
      selectedParcelView: 'all' as string
    }
  },
  computed: {
    subjectTaxHistory() {
      return usePropertyReportStore().subjectTaxHistory
    },
    parcels(): { tax_assessor_id: number; name: string; history: any[] }[] {
      if (!isLoaded(this.subjectTaxHistory)) {
        return []
      }
      return this.subjectTaxHistory.data.parcels || []
    },
    parcelViewOptions(): { label: string; value: string }[] {
      const options = [{ label: 'All Parcels (Combined)', value: 'all' }]
      for (const parcel of this.parcels) {
        options.push({
          label: `${parcel.tax_assessor_id} (${parcel.name})`,
          value: parcel.tax_assessor_id.toString()
        })
      }
      return options
    },
    sortedTaxHistory(): any[] {
      if (!isLoaded(this.subjectTaxHistory)) {
        return []
      }
      return [...this.subjectTaxHistory.data.tax_assessor_history_v2].sort(
        (a, b) => (a.assessed_tax_year || 0) - (b.assessed_tax_year || 0)
      )
    },
    displayedTaxHistory(): any[] {
      if (this.selectedParcelView === 'all') {
        return this.sortedTaxHistory
      }
      const parcel = this.parcels.find(
        (p) => p.tax_assessor_id.toString() === this.selectedParcelView
      )
      if (!parcel) {
        return this.sortedTaxHistory
      }
      return [...parcel.history].sort(
        (a, b) => (a.assessed_tax_year || 0) - (b.assessed_tax_year || 0)
      )
    },
    recordsWithMortgageData(): any[] {
      if (!isLoaded(this.subjectTaxHistory)) {
        return []
      }
      return this.subjectTaxHistory.data.recorder_v2_summary_v2.filter((record) =>
        this.hasRecordMortgageData(record)
      )
    },
    hasMortgageData(): boolean {
      return this.recordsWithMortgageData.length > 0
    }
  },
  async created() {
    await this.fetchTaxHistory()
    this.$nextTick().then(this.createTaxHistoryChart)
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
  },
  methods: {
    isError,
    isLoading,
    isLoaded,
    hasRecordMortgageData(record: any): boolean {
      return !!(
        record.cherre_latest_mortgage_amount != null ||
        record.cherre_latest_mortgage_date ||
        record.cherre_latest_mortgage_interest_rate != null ||
        record.cherre_latest_mortgage_lender ||
        record.cherre_latest_mortgage_interest_type ||
        record.cherre_latest_mortgage_term != null ||
        record.cherre_latest_mortgage_term_type ||
        record.cherre_latest_mortgage_borrower
      )
    },
    formatMoney,
    formatNumber,
    formatDate: (val: string | null): string => {
      return val ? format(parseISO(val), 'M/d/yyyy') : ''
    },
    formatPercent,
    getOwnerDisplayName(owner: TaxAssessorOwnerV2): string {
      if (owner.owner_name) {
        return owner.owner_name
      }
      const nameParts = [
        owner.owner_first_name,
        owner.owner_middle_name,
        owner.owner_last_name,
        owner.owner_name_suffix
      ].filter(Boolean)
      return nameParts.length > 0 ? nameParts.join(' ') : 'N/A'
    },
    fetchTaxHistory() {
      if (this.subjectDetail && isNotStarted(this.subjectTaxHistory)) {
        return usePropertyReportStore().getTaxHistory(this.subjectDetail)
      }
    },
    updateChart() {
      this.$nextTick().then(this.createTaxHistoryChart)
    },
    createTaxHistoryChart() {
      const el = this.$refs.taxHistoryChart as HTMLCanvasElement
      if (!el || !el.isConnected || !this.displayedTaxHistory.length) {
        return
      }

      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }

      const ctx = el.getContext('2d')
      if (!ctx) {
        return
      }

      const years = this.displayedTaxHistory.map((h) => h.assessed_tax_year?.toString() || 'N/A')
      const totalValues = this.displayedTaxHistory.map((h) => h.assessed_value_total || 0)
      const taxBillValues = this.displayedTaxHistory.map((h) => h.tax_bill_amount || 0)

      this.chartInstance = new ChartJS(ctx, {
        type: 'line',
        data: {
          labels: years,
          datasets: [
            {
              label: 'Total Assessed Value',
              data: totalValues,
              borderColor: unitTypeColor(0),
              backgroundColor: unitTypeColor(0),
              borderWidth: 3,
              tension: 0.4,
              pointBackgroundColor: unitTypeColor(0),
              pointRadius: 4,
              pointHoverRadius: 8
            },
            {
              label: 'Tax Bill',
              data: taxBillValues,
              borderColor: unitTypeColor(1),
              backgroundColor: unitTypeColor(1),
              borderWidth: 3,
              tension: 0.4,
              pointBackgroundColor: unitTypeColor(1),
              pointRadius: 4,
              pointHoverRadius: 8
            }
          ]
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (context) => {
                  const value = context.parsed.y
                  return `${context.dataset.label}: ${formatMoney(value, 0)}`
                }
              }
            }
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Tax Year'
              }
            },
            y: {
              display: true,
              title: {
                display: true,
                text: 'Amount ($)'
              },
              ticks: {
                callback(value) {
                  return formatMoney(Number(value))
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      })
    }
  }
}
</script>
<style scoped>
.title {
  font-weight: 500;
}
</style>
